<script setup lang="ts">
/**
 * Tasks Index Page
 * 
 * Main page for viewing and managing all tasks across all projects.
 * 
 * Features:
 * - Search tasks by title
 * - Filter by status, priority, assignee, project, client
 * - Inline task editing
 * - Create new tasks
 * - View task count
 * - Empty states for no tasks / no filtered results
 * - Error handling with retry capability
 * - Multi-select with bulk delete
 * - Column sorting (asc/desc)
 * - Added search functionality
 * - Added multi-criteria filtering
 * - Added priority field
 * - Added empty state components
 * - Added better error handling
 * - Added loading skeletons
 * - Added task detail modal for full task view
 * - View/edit complete task information in slide-over
 * - Added multi-select checkboxes for bulk operations
 * - Added bulk delete functionality with confirmation
 * - Added column sorting with visual indicators
 * - Sort by title, status, priority, assignee, dueDate, project, client
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import TaskTable from '@/components/tasks/TaskTable.vue';
import TaskDetailsSheet from '@/components/tasks/TaskDetailsSheet.vue';
import TaskWorkspaceControls from '@/components/tasks/TaskWorkspaceControls.vue';
import TaskGroupHeader from '@/components/tasks/TaskGroupHeader.vue';
import KanbanBoard from '@/components/tasks/KanbanBoard.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import SectionHeader from '@/components/workspace/SectionHeader.vue';
import { ListTodo } from 'lucide-vue-next';
import { buildClientSelectOptions } from '~/lib/clientOwnership';
import type { Task } from '~/types/models';
import { buildTaskAssigneePayload, resolveTaskAssigneeIds } from '~/lib/taskAssignees';
import { getTaskStatusOption, getTenantTaskStatuses } from '~/lib/taskConfig';
import { resolveProjectMilestone } from '~/lib/projectMilestones';
import { isProjectInactive } from '~/lib/projectLifecycle';
import {
    filterAndSortWorkspaceTasks,
    groupWorkspaceTasks,
    isTaskComplete,
    isTaskDueToday,
    isTaskOverdue,
    type TaskGroupBy,
    type TaskGroupSection,
} from '~/lib/taskWorkspace';

definePageMeta({
    layout: 'app',
    middleware: ['tenant']
});

// ========================================================================
// STORES & STATE
// ========================================================================

const route = useRoute();
const router = useRouter();
const taskStore = useTaskStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const projectStore = useProjectStore();
const clientStore = useClientStore();
const teamStore = useTeamStore();
const filesStore = useFilesStore();
const commentsStore = useCommentsStore();
const entityTagsStore = useEntityTagsStore();
const { canMutate } = usePermissions();
const { tenantPath } = useTenantPath();
const actionFeedback = useActionFeedback();

// Bulk action timing constants
const BULK_MESSAGE_TIMEOUT_MS = 4000;
const DELETE_COUNTDOWN_MS = 5000;

/** Loading state for initial data fetch */
const loading = ref(true);

/** Error state - stores error message if fetch fails */
const error = ref<string | null>(null);

/** View mode - table or kanban */
const viewMode = ref<'table' | 'kanban'>('table');

/** Task detail modal state */
const showTaskDetail = ref(false);
const selectedTaskId = ref<string | null>(null);

/** Multi-select state for bulk operations */
const selectedTasks = ref<string[]>([]);

/** Bulk operation loading state */
const bulkLoading = ref(false);

/** Bulk operation feedback message */
const bulkMessage = ref<{ type: 'success' | 'error'; text: string } | null>(null);
const bulkMessageTimer = ref<ReturnType<typeof setTimeout> | null>(null);

/** Undo state for bulk delete */
const pendingDelete = ref<{ ids: string[]; timer: ReturnType<typeof setTimeout> } | null>(null);

/** Sorting state */
const sortField = ref<string | null>(null);
const sortDirection = ref<'asc' | 'desc' | null>(null);
const collapsedTaskGroupIds = ref<Set<string>>(new Set());
const hasRestoredTaskViewState = ref(false);
const isSyncingTaskQuery = ref(false);
const draggingTaskGroupId = ref<string | null>(null);
const taskGroupDropTargetId = ref<string | null>(null);
const activeTaskAddGroupId = ref<string | null>(null);
const taskAddAfterTaskId = ref('');
const pendingTaskTag = ref('');
const defaultParentTasksCollapsed = computed(() => (
    (userStore.user?.preferences?.tasks?.defaultParentTaskState || 'collapsed') === 'collapsed'
));
const parentTaskStateStorageKey = computed(() => (
    typeof route.params.tenantSlug === 'string' && route.params.tenantSlug
        ? `task-table:collapsed:${route.params.tenantSlug}:tasks-index`
        : tenantStore.currentTenantId
        ? `task-table:collapsed:${tenantStore.currentTenantId}:tasks-index`
        : 'task-table:collapsed:tasks-index'
));

// ========================================================================
// TASK TABLE COMPOSABLE
// ========================================================================

/**
 * Use task table composable WITHOUT projectId
 * This means new tasks start with empty project/client fields
 * allowing users to link them as needed
 */
const {
    // Task state
    newTask,
    isAddingTask,
    editingTaskId,
    editingField,
    editingValue,
    
    // Search & Filter
    searchQuery,
    filters,
    filteredTasks,
    hasActiveFilters,
    
    // Options
    statusOptions,
    statusOptionDetails,
    assigneeOptions,
    priorityOptions,
    projectOptions,
    clientOptions,
    
    // Handlers
    startAddingTask: openNewTaskRow,
    saveNewTask,
    cancelAddingTask: closeNewTaskRow,
    startEditingField,
    saveEditedField,
    cancelEditingField,
    handleEditKeyPress,
    deleteTask,
    clearAllFilters
} = useTaskTable();

// ========================================================================
// COMPUTED PROPERTIES
// ========================================================================

const userId = computed(() => userStore.userId || 'unknown');
const statusFilterOptions = computed(() => statusOptionDetails.value?.length
    ? statusOptionDetails.value
    : statusOptions.value.map(option => ({ value: option, label: option, color: '#64748b' })));
const assigneeFilterOptions = computed(() => assigneeOptions.value.map(option => ({ value: option.id, label: option.label })));
const projectFilterOptions = computed(() => projectOptions.value.map(option => ({ value: option.id, label: option.name })));
const clientFilterOptions = computed(() => buildClientSelectOptions(clientStore.clients));
const tagFilterOptions = computed(() => entityTagsStore.tagsForType('task').map(tag => ({ value: tag, label: tag })));
const kanbanPriorityOptions = computed(() => priorityOptions.value.map(option => ({
    value: option.value,
    label: option.label,
    color: option.color || '#64748b',
})));
const presetFilter = computed(() => String(route.query.preset || '').trim().toLowerCase());
const hasPresetFilter = computed(() => Boolean(presetFilter.value));
const taskGroupByOptions: Array<{ value: TaskGroupBy; label: string }> = [
    { value: 'none', label: 'No grouping' },
    { value: 'project', label: 'Project' },
    { value: 'milestone', label: 'Milestone' },
    { value: 'status', label: 'Status' },
    { value: 'client', label: 'Client' },
    { value: 'assignee', label: 'Assigned to' },
    { value: 'overdue', label: 'Overdue / due date' },
    { value: 'tag', label: 'Tag' },
];
const validTaskGroupBy = new Set<TaskGroupBy>(taskGroupByOptions.map(option => option.value));
const taskGroupBy = computed<TaskGroupBy>(() => {
    const value = String(route.query.groupBy || '').trim().toLowerCase();
    return validTaskGroupBy.has(value as TaskGroupBy) ? (value as TaskGroupBy) : 'none';
});
const taskViewStateStorageKey = computed(() => (
    typeof route.params.tenantSlug === 'string' && route.params.tenantSlug
        ? `task-table:view:${route.params.tenantSlug}:tasks-index`
        : tenantStore.currentTenantId
        ? `task-table:view:${tenantStore.currentTenantId}:tasks-index`
        : 'task-table:view:tasks-index'
));
const taskDisplayModeStorageKey = computed(() => `${taskViewStateStorageKey.value}:display-mode`);
const hasRestoredTaskDisplayMode = ref(false);
const taskGroupCollapseStorageKey = computed(() => `${taskViewStateStorageKey.value}:collapsed-groups:${taskGroupBy.value}`);
const taskViewQueryKeys = ['groupBy', 'search', 'status', 'priority', 'assigneeId', 'projectId', 'clientId', 'tag'] as const;
const presetFilterLabel = computed(() => {
    if (presetFilter.value === 'open') return 'Open tasks';
    if (presetFilter.value === 'completed') return 'Completed tasks';
    if (presetFilter.value === 'due-today') return 'Due today';
    if (presetFilter.value === 'overdue') return 'Overdue tasks';
    if (presetFilter.value === 'due-soon') return 'Due soon';
    if (presetFilter.value === 'no-status') return 'Tasks with no status';
    return '';
});
const projectNameById = computed(() => Object.fromEntries(
    projectStore.projects.map(project => [project.id, project.name || 'Project']),
));
const projectById = computed(() => new Map(projectStore.projects.map(project => [project.id, project])));
const taskBelongsToInactiveProject = (task: Task) => {
    const project = projectById.value.get(String(task.projectId || ''));
    return Boolean(project && isProjectInactive(project.status));
};
const milestoneNameByTaskId = computed(() => Object.fromEntries(
    taskStore.tasks.map((task) => {
        const project = projectById.value.get(String(task.projectId || ''));
        const milestone = resolveProjectMilestone(project?.stages || [], task.milestoneId);
        return [task.id, milestone?.name || ''];
    }),
));
const clientNameById = computed(() => Object.fromEntries(
    clientStore.clients.map(client => [client.id, client.companyName || client.name || 'Client']),
));
const assigneeNameById = computed(() => Object.fromEntries(
    assigneeOptions.value.map(option => [option.id, option.label]),
));
const taskStatusOrder = computed(() => {
    const map = new Map<string, number>();
    getTenantTaskStatuses(tenantStore.currentTenant).forEach((status, index) => {
        const value = String(status.value || '').trim().toLowerCase();
        const label = String(status.label || '').trim().toLowerCase();
        if (value) map.set(value, index);
        if (label) map.set(label, index);
    });
    return map;
});
const currentTaskGroupingPreference = computed(() => {
    const value = String(userStore.user?.preferences?.tasks?.currentTaskGrouping || '').trim().toLowerCase();
    return validTaskGroupBy.has(value as TaskGroupBy) ? (value as TaskGroupBy) : null;
});
const taskGroupOrderOverrides = ref<Partial<Record<TaskGroupBy, string[]>>>({});
const taskGroupOrderPreference = computed(() => (
    taskGroupBy.value === 'none'
        ? []
        : (taskGroupOrderOverrides.value[taskGroupBy.value] || userStore.user?.preferences?.tasks?.taskGroupOrders?.[taskGroupBy.value] || [])
));

const openTasksCount = computed(() => taskStore.tasks.filter((task) => !taskBelongsToInactiveProject(task) && !isTaskComplete(task)).length);
const dueTodayCount = computed(() => taskStore.tasks.filter((task) => !taskBelongsToInactiveProject(task) && isTaskDueToday(task)).length);
const overdueTasksCount = computed(() => taskStore.tasks.filter((task) => !taskBelongsToInactiveProject(task) && isTaskOverdue(task)).length);
const completedTasksCount = computed(() => taskStore.tasks.filter((task) => isTaskComplete(task)).length);

/**
 * Sorted tasks - applies sorting to filtered tasks
 * 
 * Supports sorting by: title, status, priority, assignee, dueDate, projectId, clientId
 */
const sortedTasks = computed(() => {
    const taskSource = presetFilter.value === 'completed'
        ? filteredTasks.value
        : filteredTasks.value.filter(task => !taskBelongsToInactiveProject(task));
    return filterAndSortWorkspaceTasks(
        taskSource,
        presetFilter.value,
        sortField.value,
        sortDirection.value,
    );
});

const groupedTaskSections = computed(() => {
    return groupWorkspaceTasks(sortedTasks.value, taskGroupBy.value, {
        projectNameById: projectNameById.value,
        clientNameById: clientNameById.value,
        assigneeNameById: assigneeNameById.value,
        milestoneNameByTaskId: milestoneNameByTaskId.value,
        statusOrder: taskStatusOrder.value,
        preferredOrder: taskGroupOrderPreference.value,
        tagsByTaskId: Object.fromEntries(sortedTasks.value.map(task => [
            task.id,
            entityTagsStore.tagsFor('task', task.id),
        ])),
    });
});

function taskGroupColor(section: TaskGroupSection): string {
    if (taskGroupBy.value === 'tag') return section.id === 'no-tag'
        ? 'var(--muted-foreground)'
        : entityTagsStore.colorFor(section.title);
    if (taskGroupBy.value !== 'status') return 'var(--primary)';
    return getTaskStatusOption(section.id, statusOptionDetails.value)?.color
        || getTaskStatusOption(section.title, statusOptionDetails.value)?.color
        || 'var(--muted-foreground)';
}

function createBlankTaskDraft(overrides: Partial<typeof newTask.value> = {}) {
    return {
        title: '',
        status: '',
        assignee: '',
        assigneeId: '',
        assigneeIds: [] as string[],
        assignees: [] as Array<{ id: string; label: string; avatarURL?: string }>,
        priority: '' as typeof newTask.value.priority,
        startDate: '',
        dueDate: '',
        clientVisible: false,
        projectId: '',
        milestoneId: '',
        clientId: '',
        ...overrides,
    };
}

function taskDefaultsForSection(section?: TaskGroupSection | null): Partial<typeof newTask.value> {
    if (!section) return {};

    if (taskGroupBy.value === 'project' && section.id !== 'workspace') {
        return { projectId: section.id };
    }

    if (taskGroupBy.value === 'status' && section.id !== 'no-status') {
        return { status: section.id };
    }

    if (taskGroupBy.value === 'client' && clientNameById.value[section.id]) {
        return { clientId: section.id };
    }

    if (taskGroupBy.value === 'assignee' && assigneeNameById.value[section.id]) {
        const assignee = assigneeOptions.value.find((option) => option.id === section.id);
        return {
            assigneeId: section.id,
            assigneeIds: [section.id],
            assignee: assignee?.label || assigneeNameById.value[section.id] || '',
            assignees: assignee ? [assignee] : [],
        };
    }

    return {};
}

function taskTagForSection(section?: TaskGroupSection | null): string {
    if (taskGroupBy.value !== 'tag' || !section || section.id === 'no-tag') return '';
    return section.title;
}

function taskDefaultsAfterTask(task: Task): Partial<typeof newTask.value> {
    const assigneeIds = resolveTaskAssigneeIds(task);
    const assignees = assigneeIds
        .map((id) => assigneeOptions.value.find((option) => option.id === id))
        .filter(Boolean) as Array<{ id: string; label: string; avatarURL?: string }>;

    return {
        status: String(task.status || ''),
        projectId: String(task.projectId || ''),
        milestoneId: String(task.milestoneId || ''),
        clientId: String(task.clientId || ''),
        assigneeId: assigneeIds[0] || '',
        assigneeIds,
        assignee: assignees[0]?.label || String(task.assignee || ''),
        assignees,
        clientVisible: task.clientVisible === true,
    };
}

function startAddingTask(section?: TaskGroupSection | null) {
    activeTaskAddGroupId.value = section?.id || 'all';
    taskAddAfterTaskId.value = '';
    pendingTaskTag.value = taskTagForSection(section);
    newTask.value = createBlankTaskDraft(taskDefaultsForSection(section));
    openNewTaskRow();
}

function handleInsertTaskAfter(task: Task, section?: TaskGroupSection | null) {
    activeTaskAddGroupId.value = section?.id || 'all';
    taskAddAfterTaskId.value = task.id;
    pendingTaskTag.value = taskTagForSection(section);
    newTask.value = createBlankTaskDraft({
        ...taskDefaultsForSection(section),
        ...taskDefaultsAfterTask(task),
    });
    openNewTaskRow();
}

function resetTaskAddPlacement() {
    activeTaskAddGroupId.value = null;
    taskAddAfterTaskId.value = '';
    pendingTaskTag.value = '';
}

function cancelTaskAdd() {
    closeNewTaskRow();
    resetTaskAddPlacement();
}

function handleTaskAddKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        handleSaveTask();
    } else if (event.key === 'Escape') {
        cancelTaskAdd();
    }
}

const taskTableShowProjectColumn = computed(() => taskGroupBy.value !== 'project');
const taskTableShowClientColumn = computed(() => taskGroupBy.value !== 'client');

async function updateTaskGrouping(value: string) {
    const next = validTaskGroupBy.has(value as TaskGroupBy) ? (value as TaskGroupBy) : 'none';
    const query = { ...route.query };
    if (next === 'none') {
        delete query.groupBy;
    } else {
        query.groupBy = next;
    }
    await router.replace({ path: route.path, query });
    await userStore.updateTaskPreferences({
        currentTaskGrouping: next,
    });
}

function normalizeTaskViewQuery(input: Record<string, unknown>) {
    const query: Record<string, string> = {};
    const groupBy = String(input.groupBy || '').trim().toLowerCase();
    if (validTaskGroupBy.has(groupBy as TaskGroupBy) && groupBy !== 'none') query.groupBy = groupBy;

    for (const key of taskViewQueryKeys) {
        if (key === 'groupBy') continue;
        const value = typeof input[key] === 'string' ? input[key].trim() : '';
        if (value) query[key] = value;
    }

    return query;
}

function hasTaskViewQuery(query: typeof route.query) {
    return taskViewQueryKeys.some((key) => typeof query[key] === 'string' && String(query[key]).trim());
}

function persistTaskViewState(query: Record<string, unknown>) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(taskViewStateStorageKey.value, JSON.stringify(normalizeTaskViewQuery(query)));
    } catch {
        // Local persistence is optional.
    }
}

async function restoreTaskViewState() {
    if (typeof window === 'undefined') {
        hasRestoredTaskViewState.value = true;
        return;
    }

    if (hasTaskViewQuery(route.query)) {
        persistTaskViewState(route.query);
        hasRestoredTaskViewState.value = true;
        return;
    }

    try {
        const raw = window.localStorage.getItem(taskViewStateStorageKey.value);
        const parsed = raw ? JSON.parse(raw) : null;
        const restoredQuery = normalizeTaskViewQuery(parsed || {});
        if (currentTaskGroupingPreference.value) {
            if (currentTaskGroupingPreference.value === 'none') {
                delete restoredQuery.groupBy;
            } else {
                restoredQuery.groupBy = currentTaskGroupingPreference.value;
            }
        } else if (Object.keys(restoredQuery).length === 0) {
            const preferredGrouping = String(userStore.user?.preferences?.tasks?.defaultTaskGrouping || '').trim().toLowerCase();
            if (validTaskGroupBy.has(preferredGrouping as TaskGroupBy) && preferredGrouping !== 'none') {
                restoredQuery.groupBy = preferredGrouping;
            }
        }
        if (Object.keys(restoredQuery).length > 0) {
            isSyncingTaskQuery.value = true;
            await router.replace({ path: route.path, query: { ...route.query, ...restoredQuery } });
            isSyncingTaskQuery.value = false;
        }
    } catch {
        // Ignore invalid stored state and keep the default view.
    } finally {
        hasRestoredTaskViewState.value = true;
        isSyncingTaskQuery.value = false;
    }
}

async function persistTaskGroupOrder(order: string[]) {
    if (taskGroupBy.value === 'none') return;
    taskGroupOrderOverrides.value = {
        ...taskGroupOrderOverrides.value,
        [taskGroupBy.value]: order,
    };
    await userStore.updateTaskPreferences({
        taskGroupOrders: {
            ...(userStore.user?.preferences?.tasks?.taskGroupOrders || {}),
            [taskGroupBy.value]: order,
        },
    });
}

function startTaskGroupDrag(groupId: string) {
    draggingTaskGroupId.value = groupId;
    taskGroupDropTargetId.value = null;
}

function setTaskGroupDropTarget(groupId: string) {
    if (!draggingTaskGroupId.value || draggingTaskGroupId.value === groupId) return;
    taskGroupDropTargetId.value = groupId;
}

async function handleTaskGroupDrop(targetGroupId: string) {
    const sourceGroupId = draggingTaskGroupId.value;
    draggingTaskGroupId.value = null;
    taskGroupDropTargetId.value = null;

    if (!sourceGroupId || sourceGroupId === targetGroupId || taskGroupBy.value === 'none') {
        return;
    }

    const currentOrder = groupedTaskSections.value.map((section) => section.id);
    const fromIndex = currentOrder.indexOf(sourceGroupId);
    const toIndex = currentOrder.indexOf(targetGroupId);
    if (fromIndex < 0 || toIndex < 0) return;

    const nextOrder = [...currentOrder];
    const [moved] = nextOrder.splice(fromIndex, 1);
    if (!moved) return;
    nextOrder.splice(toIndex, 0, moved);

    await persistTaskGroupOrder(nextOrder);
}

async function applyUserDefaultTaskGrouping(groupingValue?: string | null) {
    if (typeof window === 'undefined' || hasTaskViewQuery(route.query)) return;

    try {
        const raw = window.localStorage.getItem(taskViewStateStorageKey.value);
        const parsed = raw ? JSON.parse(raw) : null;
        const restoredQuery = normalizeTaskViewQuery(parsed || {});
        if (Object.keys(restoredQuery).length > 0) {
            return;
        }
    } catch {
        // Ignore invalid stored state and fall through to user defaults.
    }

    const preferredGrouping = String(groupingValue || '').trim().toLowerCase();
    if (!validTaskGroupBy.has(preferredGrouping as TaskGroupBy)) {
        return;
    }

    if (taskGroupBy.value === preferredGrouping) {
        return;
    }

    isSyncingTaskQuery.value = true;
    const query = { ...route.query };
    if (preferredGrouping === 'none') delete query.groupBy;
    else query.groupBy = preferredGrouping;
    await router.replace({ path: route.path, query });
    isSyncingTaskQuery.value = false;
}

function queryValuesEqual(a: Record<string, unknown>, b: Record<string, unknown>) {
    return taskViewQueryKeys.every((key) => String(a[key] || '') === String(b[key] || ''));
}

function isTaskGroupCollapsed(groupId: string) {
    return collapsedTaskGroupIds.value.has(groupId);
}

function loadCollapsedTaskGroups() {
    if (typeof window === 'undefined') return;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(taskGroupCollapseStorageKey.value) || '[]');
        collapsedTaskGroupIds.value = new Set(Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []);
    } catch {
        collapsedTaskGroupIds.value = new Set();
    }
}

function persistCollapsedTaskGroups() {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(taskGroupCollapseStorageKey.value, JSON.stringify(Array.from(collapsedTaskGroupIds.value)));
    } catch {
        // Local persistence is optional.
    }
}

function toggleTaskGroupCollapse(groupId: string) {
    const next = new Set(collapsedTaskGroupIds.value);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    collapsedTaskGroupIds.value = next;
    persistCollapsedTaskGroups();
}

/**
 * Task count for display
 * Shows filtered count vs total count
 */
const taskCount = computed(() => {
    const total = taskStore.tasks.length;
    const filtered = sortedTasks.value.length;
    
    if (hasActiveFilters.value && filtered !== total) {
        return `${filtered} of ${total}`;
    }
    return `${total}`;
});

// ========================================================================
// LIFECYCLE & DATA LOADING
// ========================================================================

/**
 * Load initial data on mount
 * 
 * Fetches tasks, projects, and clients in parallel for better performance.
 * Handles errors gracefully and provides retry capability.
 */
onMounted(async () => {
    try {
        const storedMode = window.localStorage.getItem(taskDisplayModeStorageKey.value);
        viewMode.value = storedMode === 'table' || storedMode === 'kanban'
            ? storedMode
            : window.matchMedia('(max-width: 767px)').matches
                ? 'kanban'
                : 'table';
    } catch {
        viewMode.value = window.matchMedia('(max-width: 767px)').matches ? 'kanban' : 'table';
    } finally {
        hasRestoredTaskDisplayMode.value = true;
    }
    await restoreTaskViewState();
    loadCollapsedTaskGroups();
    await loadData();
});

watch(
    () => route.query,
    (query) => {
        filters.value.status = typeof query.status === 'string' ? query.status : '';
        filters.value.priority = typeof query.priority === 'string' ? query.priority : '';
        filters.value.assigneeId = typeof query.assigneeId === 'string' ? query.assigneeId : '';
        filters.value.projectId = typeof query.projectId === 'string' ? query.projectId : '';
        filters.value.clientId = typeof query.clientId === 'string' ? query.clientId : '';
        filters.value.tag = typeof query.tag === 'string' ? query.tag : '';
        searchQuery.value = typeof query.search === 'string' ? query.search : '';
        if (hasRestoredTaskViewState.value && !isSyncingTaskQuery.value) {
            persistTaskViewState(query);
        }
    },
    { immediate: true },
);

watch(
    () => [
        searchQuery.value,
        filters.value.status,
        filters.value.priority,
        filters.value.assigneeId,
        filters.value.projectId,
        filters.value.clientId,
        filters.value.tag,
    ],
    async ([search, status, priority, assigneeId, projectId, clientId, tag]) => {
        if (!hasRestoredTaskViewState.value || isSyncingTaskQuery.value) return;

        const query = { ...route.query };
        const nextValues = { search, status, priority, assigneeId, projectId, clientId, tag };
        for (const [key, value] of Object.entries(nextValues)) {
            if (value) query[key] = value;
            else delete query[key];
        }

        persistTaskViewState(query);
        if (queryValuesEqual(route.query, query)) return;

        isSyncingTaskQuery.value = true;
        await router.replace({ path: route.path, query });
        isSyncingTaskQuery.value = false;
    },
);

watch(taskGroupBy, () => {
    collapsedTaskGroupIds.value = new Set();
    draggingTaskGroupId.value = null;
    taskGroupDropTargetId.value = null;
    loadCollapsedTaskGroups();
});

watch(
    () => currentTaskGroupingPreference.value,
    async (groupingValue) => {
        if (!hasRestoredTaskViewState.value || typeof route.query.groupBy === 'string' || !groupingValue) return;
        await applyUserDefaultTaskGrouping(groupingValue);
    },
);

watch(
    () => userStore.user?.preferences?.tasks?.defaultTaskGrouping,
    async (groupingValue) => {
        if (!hasRestoredTaskViewState.value || typeof route.query.groupBy === 'string' || currentTaskGroupingPreference.value) return;
        await applyUserDefaultTaskGrouping(groupingValue);
    },
);

onUnmounted(() => {
    if (bulkMessageTimer.value !== null) clearTimeout(bulkMessageTimer.value);
    if (pendingDelete.value) clearTimeout(pendingDelete.value.timer);
});

/**
 * Load or reload all data
 * Can be called from retry button on error
 */
async function loadData() {
    loading.value = true;
    error.value = null;
    
    try {
        if (!tenantStore.currentTenantId) {
            throw new Error('No tenant selected. Please log in.');
        }
        
        // Load all data in parallel for better performance
        await Promise.all([
            taskStore.fetchTasks(),
            projectStore.fetchProjects(),
            clientStore.fetchClients(),
            teamStore.fetchTeamMembers(tenantStore.currentTenantId),
            entityTagsStore.fetchEntityTags(),
        ]);

        if (viewMode.value === 'table') {
            const taskIds = taskStore.tasks.map(task => task.id);
            await Promise.all([
                filesStore.fetchFilesForEntities('task', taskIds),
                commentsStore.fetchCommentsForEntities('task', taskIds),
            ]);
        }

        if (taskStore.taskError) {
            throw new Error(taskStore.taskError);
        }

        if (typeof route.query.taskId === 'string') {
            handleViewTask(route.query.taskId);
        }
    } catch (e: any) {
        error.value = e.message || 'Failed to load tasks. Please try again.';
        console.error('Error loading tasks:', e);
    } finally {
        loading.value = false;
    }
}

watch(viewMode, (mode) => {
    if (hasRestoredTaskDisplayMode.value) {
        try {
            window.localStorage.setItem(taskDisplayModeStorageKey.value, mode);
        } catch {
            // Local persistence is optional.
        }
    }
    if (mode !== 'table' || taskStore.tasks.length === 0) return;
    const taskIds = taskStore.tasks.map(task => task.id);
    void Promise.all([
        filesStore.fetchFilesForEntities('task', taskIds),
        commentsStore.fetchCommentsForEntities('task', taskIds),
    ]);
});

// ========================================================================
// EVENT HANDLERS
// ========================================================================

/**
 * Handle saving a new task
 * 
 * Saves the task then refreshes the list to include the new task.
 * The composable handles resetting the form and exiting add mode.
 */
async function handleSaveTask() {
    if (!canMutate.value) {
        error.value = 'You do not have permission to create tasks.';
        return;
    }
    try {
        const taskId = await saveNewTask(userId.value);
        if (taskId && pendingTaskTag.value) {
            try {
                await entityTagsStore.setEntityTags('task', taskId, [pendingTaskTag.value]);
            } catch (tagError) {
                await taskStore.fetchTasks();
                resetTaskAddPlacement();
                error.value = tagError instanceof Error
                    ? `Task created, but its tag could not be applied: ${tagError.message}`
                    : 'Task created, but its tag could not be applied.';
                return;
            }
        }
        await taskStore.fetchTasks();
        resetTaskAddPlacement();
    } catch (e: any) {
        error.value = e.message || 'Failed to save task';
    }
}

/**
 * Handle deleting a task
 * 
 * @param taskId - ID of task to delete
 */
async function handleDeleteTask(taskId: string) {
    if (!canMutate.value) {
        error.value = 'You do not have permission to delete tasks.';
        return;
    }
    try {
        await deleteTask(taskId);
        // No need to refresh - store removes it from local state
    } catch (e: any) {
        error.value = e.message || 'Failed to delete task';
    }
}

/**
 * Handle opening task detail modal
 * 
 * @param taskId - ID of task to view
 */
function handleViewTask(taskId: string) {
    selectedTaskId.value = taskId;
    showTaskDetail.value = true;
}

/**
 * Handle task update from detail modal
 * 
 * Refreshes the task list to show updated data
 */
async function handleTaskUpdated() {
    await taskStore.fetchTasks();
}

/**
 * Handle task deletion from detail modal
 * 
 * Refreshes the task list and closes modal
 */
async function handleTaskDeleted() {
    showTaskDetail.value = false;
    selectedTaskId.value = null;
    // No need to refresh - store removes it from local state
}

/**
 * Handle task status update from kanban board
 * 
 * Updates the task status and refreshes the list
 * 
 * @param taskId - ID of task to update
 * @param newStatus - New status value
 */
async function handleKanbanTaskUpdate(taskId: string, newStatus: string) {
    const completed = isTaskComplete({ status: newStatus } as Task);
    const feedbackId = actionFeedback.loading(
        completed ? 'Completing task…' : 'Updating task…',
        'Saving the new status for everyone in the workspace.',
    );
    try {
        await taskStore.updateTask(taskId, { status: newStatus });
        actionFeedback.success(
            feedbackId,
            completed ? 'Task completed' : 'Task updated',
            completed ? 'The task is complete and no longer needs attention.' : 'The new status is now visible to the team.',
        );
    } catch (err: any) {
        console.error('Failed to update task status:', err);
        actionFeedback.error(
            feedbackId,
            'Task status was not updated',
            err?.message || 'Your previous task status has been kept.',
            () => void handleKanbanTaskUpdate(taskId, newStatus),
        );
    }
}

// ========================================================================
// MULTI-SELECT & SORTING HANDLERS
// ========================================================================

/**
 * Handle toggling selection of a single task
 * 
 * @param taskId - ID of task to toggle
 */
function handleToggleSelect(taskId: string) {
    const index = selectedTasks.value.indexOf(taskId);
    if (index > -1) {
        selectedTasks.value.splice(index, 1);
    } else {
        selectedTasks.value.push(taskId);
    }
}

/**
 * Handle toggling selection of all tasks
 * 
 * If all tasks are selected, deselects all.
 * If some or none are selected, selects all visible tasks.
 */
function handleToggleSelectAll(taskIds?: string[]) {
    const ids = taskIds ?? sortedTasks.value.map(t => t.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedTasks.value.includes(id));
    if (allSelected) {
        selectedTasks.value = selectedTasks.value.filter(id => !ids.includes(id));
    } else {
        const merged = new Set([...selectedTasks.value, ...ids]);
        selectedTasks.value = Array.from(merged);
    }
}

/**
 * Show a transient bulk feedback message, auto-clearing after a delay.
 */
function showBulkMessage(type: 'success' | 'error', text: string) {
    if (bulkMessageTimer.value !== null) clearTimeout(bulkMessageTimer.value);
    bulkMessage.value = { type, text };
    bulkMessageTimer.value = setTimeout(() => {
        bulkMessage.value = null;
        bulkMessageTimer.value = null;
    }, BULK_MESSAGE_TIMEOUT_MS);
}

async function handleDeleteSelected() {
    if (!canMutate.value) {
        showBulkMessage('error', 'You do not have permission to delete tasks.');
        return;
    }
    const count = selectedTasks.value.length;
    if (count === 0) return;

    // Cancel any existing pending delete
    if (pendingDelete.value) {
        clearTimeout(pendingDelete.value.timer);
        pendingDelete.value = null;
    }

    const idsToDelete = [...selectedTasks.value];

    // Clear selection immediately for UX
    selectedTasks.value = [];

    // Start a countdown; if the user clicks Undo the timer is cancelled
    const timer = setTimeout(async () => {
        pendingDelete.value = null;
        bulkLoading.value = true;
        try {
            await Promise.all(idsToDelete.map(taskId => taskStore.deleteTask(taskId)));
            showBulkMessage('success', `Deleted ${count} task${count > 1 ? 's' : ''}`);
        } catch (e: any) {
            showBulkMessage('error', e.message || 'Failed to delete tasks');
            // Restore selection so user can retry
            selectedTasks.value = idsToDelete;
        } finally {
            bulkLoading.value = false;
        }
    }, DELETE_COUNTDOWN_MS);

    pendingDelete.value = { ids: idsToDelete, timer };
    showBulkMessage('success', `${count} task${count > 1 ? 's' : ''} will be deleted. Click Undo to cancel.`);
}

/**
 * Cancel the pending delete and restore the selection.
 */
function undoDelete() {
    if (!pendingDelete.value) return;
    clearTimeout(pendingDelete.value.timer);
    selectedTasks.value = pendingDelete.value.ids;
    pendingDelete.value = null;
    bulkMessage.value = null;
}

/**
 * Create a subtask under a parent task
 */
async function handleCreateSubtask(parentTask: Task) {
    try {
        const baseTitle = parentTask.title || 'Task';
        await taskStore.addTask({
            title: `${baseTitle} - Subtask`,
            status: parentTask.status || 'Todo',
            assignee: parentTask.assignee || '',
            assigneeId: parentTask.assigneeId || '',
            assigneeIds: parentTask.assigneeIds || resolveTaskAssigneeIds(parentTask),
            assignees: parentTask.assignees || [],
            ...(parentTask.priority ? { priority: parentTask.priority } : {}),
            dueDate: parentTask.dueDate || '',
            projectId: parentTask.projectId || '',
            milestoneId: parentTask.milestoneId || '',
            clientId: parentTask.clientId || '',
            clientVisible: parentTask.clientVisible === true,
            description: '',
            subtaskOfId: parentTask.id,
        }, userId.value);
    } catch (e: any) {
        error.value = e.message || 'Failed to create subtask';
    }
}

async function handleReparentTask(taskId: string, parentTaskId: string) {
    const parentTask = taskStore.tasks.find(task => task.id === parentTaskId);
    if (!parentTask) return;

    try {
        await taskStore.updateTask(taskId, {
            subtaskOfId: parentTask.id,
            projectId: parentTask.projectId || '',
            milestoneId: parentTask.milestoneId || '',
            clientId: parentTask.clientId || '',
        });
        await taskStore.fetchTasks();
    } catch (e: any) {
        error.value = e.message || 'Failed to move task under parent';
    }
}

async function handleClearTaskParent(taskId: string) {
    try {
        await taskStore.updateTask(taskId, {
            subtaskOfId: '',
        });
        await taskStore.fetchTasks();
    } catch (e: any) {
        error.value = e.message || 'Failed to move task back to top level';
    }
}

/**
 * Handle bulk update of selected tasks
 * 
 * @param field - Field to update (status, priority, assignee, projectId, clientId)
 * @param value - New value
 */
async function handleBulkUpdate(field: string, value: any) {
    const count = selectedTasks.value.length;
    bulkLoading.value = true;
    try {
        const payload = field === 'assigneeId'
            ? {
                assigneeId: value,
                assignee: assigneeOptions.value.find((option) => option.id === value)?.label || '',
            }
            : { [field]: value };

        // Update all selected tasks
        await Promise.all(
            selectedTasks.value.map(taskId => 
                taskStore.updateTask(taskId, payload)
            )
        );
        
        // Clear selection after successful update
        selectedTasks.value = [];
        showBulkMessage('success', `Updated ${count} task${count > 1 ? 's' : ''}`);
    } catch (e: any) {
        showBulkMessage('error', e.message || 'Failed to update tasks');
    } finally {
        bulkLoading.value = false;
    }
}

async function handleSaveAssignees(taskId: string, assigneeIds: string[]) {
    try {
        await taskStore.updateTask(taskId, buildTaskAssigneePayload(assigneeIds, assigneeOptions.value));
    } catch (e: any) {
        showBulkMessage('error', e.message || 'Failed to update assignees');
    }
}

/**
 * Update inline edit value from table events
 */
function handleEditValueChange(value: string) {
    editingValue.value = value;
}

/**
 * Update new task draft from table events
 */
function handleNewTaskChange(value: typeof newTask.value) {
    newTask.value = value;
}

/**
 * Handle sorting by a field
 */
function handleSort(field: string) {
    if (sortField.value === field) {
        // Cycle through: asc -> desc -> none
        if (sortDirection.value === 'asc') {
            sortDirection.value = 'desc';
        } else if (sortDirection.value === 'desc') {
            sortField.value = null;
            sortDirection.value = null;
        }
    } else {
        sortField.value = field;
        sortDirection.value = 'asc';
    }
}

function handleTaskFilterUpdate(
    field: 'status' | 'priority' | 'assigneeId' | 'projectId' | 'clientId' | 'tag',
    value: string,
) {
    filters.value[field] = value;
}

</script>

<template>
    <div class="page-shell">
        <div class="overview-metrics-grid">
            <MetricCard density="compact" label="Open tasks" :value="openTasksCount" :href="tenantPath({ path: '/tasks', query: { preset: 'open' } })" />
            <MetricCard density="compact" label="Due today" :value="dueTodayCount" :href="tenantPath({ path: '/tasks', query: { preset: 'due-today' } })" />
            <MetricCard density="compact" label="Overdue" :value="overdueTasksCount" :href="tenantPath({ path: '/tasks', query: { preset: 'overdue' } })" />
            <MetricCard density="compact" label="Completed" :value="completedTasksCount" :href="tenantPath({ path: '/tasks', query: { preset: 'completed' } })" />
        </div>

        <EmptyState
            v-if="error"
            title="Tasks could not be loaded"
            :description="error"
        >
            <Button variant="outline" @click="loadData">Try again</Button>
        </EmptyState>

        <!-- Main Content -->
        <div v-else class="space-y-4">
            <SectionHeader
                title="All Tasks"
                :description="`${taskCount} tasks across the workspace, with filters and inline editing kept close to the work.`"
                :icon="ListTodo"
            />

            <TaskWorkspaceControls
                :view-mode="viewMode"
                :group-by="taskGroupBy"
                :group-options="taskGroupByOptions"
                :task-count="taskCount"
                :search-query="searchQuery"
                :filters="filters"
                :has-active-filters="hasActiveFilters"
                :status-options="statusFilterOptions"
                :priority-options="priorityOptions"
                :assignee-options="assigneeFilterOptions"
                :project-options="projectFilterOptions"
                :client-options="clientFilterOptions"
                :tag-options="tagFilterOptions"
                @update:view-mode="value => viewMode = value"
                @update:group-by="updateTaskGrouping"
                @update:search-query="value => searchQuery = value"
                @update:filter="handleTaskFilterUpdate"
                @clear-filters="clearAllFilters"
            />

            <!-- Task Table -->
            <div v-if="viewMode === 'table'" class="space-y-8">
                <template v-if="taskGroupBy === 'none'">
                    <TaskTable
                        :tasks="sortedTasks"
                        :status-options="statusOptions"
                        :status-option-details="statusOptionDetails"
                        :assignee-options="assigneeOptions"
                        :priority-options="priorityOptions"
                        :project-options="projectOptions"
                        :client-options="clientOptions"
                        :show-project-column="true"
                        :show-client-column="true"
                        :is-adding="isAddingTask && activeTaskAddGroupId === 'all'"
                        :add-after-task-id="activeTaskAddGroupId === 'all' ? taskAddAfterTaskId : ''"
                        :new-task="newTask"
                        :editing-id="editingTaskId"
                        :editing-field="editingField"
                        :editing-value="editingValue"
                        :selected-tasks="selectedTasks"
                        :bulk-loading="bulkLoading"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        :default-parent-tasks-collapsed="defaultParentTasksCollapsed"
                        :parent-task-state-storage-key="parentTaskStateStorageKey"
                        :prefetch-task-metadata="false"
                        @start-edit="startEditingField"
                        @save-edit="saveEditedField()"
                        @save-assignees="handleSaveAssignees"
                        @cancel-edit="cancelEditingField"
                        @delete-task="handleDeleteTask"
                        @view-task="handleViewTask"
                        @start-add="startAddingTask()"
                        @insert-after-task="(task) => handleInsertTaskAfter(task)"
                        @edit-value-change="handleEditValueChange"
                        @new-task-change="handleNewTaskChange"
                        @edit-keydown="(e) => handleEditKeyPress(e)"
                        @add-keydown="handleTaskAddKeyPress"
                        @cancel-add="cancelTaskAdd"
                        @toggle-select="handleToggleSelect"
                        @toggle-select-all="handleToggleSelectAll"
                        @delete-selected="handleDeleteSelected"
                        @bulk-update="handleBulkUpdate"
                        @sort="handleSort"
                        @create-subtask="handleCreateSubtask"
                        @reparent-task="handleReparentTask"
                        @clear-parent="handleClearTaskParent"
                    />
                </template>

                <section
                    v-else
                    v-for="section in groupedTaskSections"
                    :key="section.id"
                    class="space-y-4"
                >
                    <TaskGroupHeader
                        :title="section.title"
                        :count="section.tasks.length"
                        :collapsed="isTaskGroupCollapsed(section.id)"
                        :color="taskGroupColor(section)"
                        :draggable="taskGroupBy !== 'overdue'"
                        :drop-target="taskGroupDropTargetId === section.id"
                        @toggle="toggleTaskGroupCollapse(section.id)"
                        @drag-start="startTaskGroupDrag(section.id)"
                        @drag-end="draggingTaskGroupId = null; taskGroupDropTargetId = null"
                        @drag-over="setTaskGroupDropTarget(section.id)"
                        @drag-leave="taskGroupDropTargetId === section.id ? taskGroupDropTargetId = null : null"
                        @drop="handleTaskGroupDrop(section.id)"
                    />

                    <TaskTable
                        v-if="!isTaskGroupCollapsed(section.id)"
                        :tasks="section.tasks"
                        :status-options="statusOptions"
                        :status-option-details="statusOptionDetails"
                        :assignee-options="assigneeOptions"
                        :priority-options="priorityOptions"
                        :project-options="projectOptions"
                        :client-options="clientOptions"
                        :show-project-column="taskTableShowProjectColumn"
                        :show-client-column="taskTableShowClientColumn"
                        :is-adding="isAddingTask && activeTaskAddGroupId === section.id"
                        :add-after-task-id="activeTaskAddGroupId === section.id ? taskAddAfterTaskId : ''"
                        :new-task="newTask"
                        :editing-id="editingTaskId"
                        :editing-field="editingField"
                        :editing-value="editingValue"
                        :selected-tasks="selectedTasks"
                        :bulk-loading="bulkLoading"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        :default-parent-tasks-collapsed="defaultParentTasksCollapsed"
                        :parent-task-state-storage-key="`${parentTaskStateStorageKey}:${section.id}`"
                        :prefetch-task-metadata="false"
                        @start-edit="startEditingField"
                        @save-edit="saveEditedField()"
                        @save-assignees="handleSaveAssignees"
                        @cancel-edit="cancelEditingField"
                        @delete-task="handleDeleteTask"
                        @view-task="handleViewTask"
                        @start-add="startAddingTask(section)"
                        @insert-after-task="(task) => handleInsertTaskAfter(task, section)"
                        @edit-value-change="handleEditValueChange"
                        @new-task-change="handleNewTaskChange"
                        @edit-keydown="(e) => handleEditKeyPress(e)"
                        @add-keydown="handleTaskAddKeyPress"
                        @cancel-add="cancelTaskAdd"
                        @toggle-select="handleToggleSelect"
                        @toggle-select-all="handleToggleSelectAll"
                        @delete-selected="handleDeleteSelected"
                        @bulk-update="handleBulkUpdate"
                        @sort="handleSort"
                        @create-subtask="handleCreateSubtask"
                        @reparent-task="handleReparentTask"
                        @clear-parent="handleClearTaskParent"
                    />
                </section>
            </div>

            <!-- Kanban Board -->
            <div v-else-if="viewMode === 'kanban'" class="mt-4">
                <KanbanBoard
                    :tasks="sortedTasks"
                    :status-columns="statusOptions"
                    :status-option-details="statusOptionDetails"
                    :priority-options="kanbanPriorityOptions"
                    :project-options="projectOptions"
                    @view-task="handleViewTask"
                    @task-updated="handleKanbanTaskUpdate"
                />
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="py-12 text-center text-muted-foreground">
                Loading tasks...
            </div>

            <!-- Empty State - No Tasks at All -->
            <EmptyState 
                v-else-if="taskStore.tasks.length === 0 && !isAddingTask"
                artwork
                artwork-seed="workspace tasks"
                title="No tasks yet"
                description="Create the first task to give the team a clear next action, owner and deadline."
            >
                <template #action>
                    <Button v-if="canMutate" @click="startAddingTask()">
                        Create task
                    </Button>
                </template>
            </EmptyState>

            <!-- Empty State - No Results from Filters -->
            <EmptyState 
                v-else-if="sortedTasks.length === 0 && (hasActiveFilters || hasPresetFilter) && !isAddingTask"
                title="No matching tasks"
                description="Try adjusting your filters or search query to find what you're looking for."
            >
                <template #action>
                    <Button @click="clearAllFilters" variant="outline">
                        Clear All Filters
                    </Button>
                </template>
            </EmptyState>

        </div>

        <!-- Task Detail Modal -->
        <TaskDetailsSheet
            v-model:open="showTaskDetail"
            :task-id="selectedTaskId"
            :status-options="statusOptions"
            :status-option-details="statusOptionDetails"
            :assignee-options="assigneeOptions"
            :priority-options="priorityOptions"
            :project-options="projectOptions"
            :client-options="clientOptions"
            @task-updated="handleTaskUpdated"
            @task-deleted="handleTaskDeleted"
        />

        <!-- Bulk Operation Feedback Toast -->
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="translate-y-4 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-4 opacity-0"
        >
            <div
                v-if="bulkMessage"
                :class="[
                    'fixed bottom-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl text-sm font-medium pointer-events-auto',
                    bulkMessage.type === 'success' ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
                ]"
            >
                <span>{{ bulkMessage.text }}</span>
                <button
                    v-if="pendingDelete"
                    @click="undoDelete"
                    class="ml-2 underline underline-offset-2 hover:no-underline font-semibold"
                >
                    Undo
                </button>
                <button
                    @click="bulkMessage = null"
                    class="ml-1 opacity-70 hover:opacity-100"
                    aria-label="Dismiss"
                >
                    ✕
                </button>
            </div>
        </Transition>
    </div>
</template>
